import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from '@mui/material';
import SoftButton from 'components/SoftButton';
import ResolutionNoteField from './ResolutionNoteField';

const path = 'POLICY_CANCELLATION.SUMMARY';

const SummaryRow = ({ label, value }) =>
  value ? (
    <Box display="flex" justifyContent="space-between" gap={2} mb={0.75}>
      <Typography variant="body2" color="grey.700">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">
        {value}
      </Typography>
    </Box>
  ) : null;

SummaryRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
};

// Shared confirmation shown before cancelling (irreversible) or submitting a cancellation request.
const CancellationSummaryDialog = ({
  open = false,
  variant,
  summary = {},
  onClose,
  onConfirm,
  withNote = false,
}) => {
  const { t } = useTranslation();
  const isCancel = variant === 'cancel';
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setNote('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        {isCancel ? t(`${path}.cancelTitle`) : t(`${path}.submitTitle`)}
      </DialogTitle>
      <DialogContent>
        <SummaryRow
          label={t(`${path}.policyNumber`)}
          value={summary.policyNumber}
        />
        <SummaryRow
          label={t(`${path}.requestDate`)}
          value={summary.requestDate}
        />
        <SummaryRow
          label={t(`${path}.refundAmount`)}
          value={summary.refundAmount}
        />
        <SummaryRow label={t(`${path}.reason`)} value={summary.reason} />
        <SummaryRow
          label={t(`${path}.methodReceived`)}
          value={summary.methodReceived}
        />
        <SummaryRow
          label={t(`${path}.delayReason`)}
          value={summary.delayReason}
        />
        <SummaryRow
          label={t(`${path}.submissionReason`)}
          value={summary.submissionReason}
        />
        <SummaryRow
          label={t(`${path}.refundEligibility`)}
          value={summary.refundEligibility}
        />
        <SummaryRow
          label={t(`${path}.attachments`)}
          value={summary.attachments}
        />
        {isCancel && (
          <Typography
            variant="body2"
            color="error"
            fontWeight={700}
            sx={{ mt: 2 }}
          >
            {t(`${path}.notReversible`)}
          </Typography>
        )}
        {withNote && (
          <ResolutionNoteField
            value={note}
            onChange={setNote}
            label={t(`${path}.notes`)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <SoftButton
          size="small"
          variant="outlined"
          color="secondary"
          onClick={onClose}
        >
          {t('COMMON.cancel')}
        </SoftButton>
        <SoftButton
          size="small"
          variant="contained"
          color={isCancel ? 'error' : 'info'}
          onClick={() => onConfirm(note.trim())}
        >
          {t('COMMON.confirm')}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

CancellationSummaryDialog.propTypes = {
  open: PropTypes.bool,
  variant: PropTypes.oneOf(['cancel', 'submit']).isRequired,
  summary: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  withNote: PropTypes.bool,
};

export default CancellationSummaryDialog;
